import React, { useState } from "react";
import { Box, ButtonBase, IconButton, Tooltip, Typography } from "@mui/material";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

export const normalizeMime = (mime = "") =>
  mime.toString().toLowerCase().split(";")[0].trim();

export const isImageAttachment = (attachment) => {
  if (!attachment) return false;
  const mime = normalizeMime(attachment.mime || "");
  if (mime.startsWith("image/")) return true;
  return attachment.kind === "image";
};

export const isAudioAttachment = (attachment) => {
  if (!attachment) return false;
  const mime = normalizeMime(attachment.mime || "");
  return mime.startsWith("audio/");
};

export const isViewableDocumentAttachment = (attachment) => {
  const mime = normalizeMime(attachment?.mime || "");
  const previewMime = normalizeMime(
    attachment?.previewMime || attachment?.preview_mime || "",
  );
  return (
    mime === "application/pdf" ||
    (mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" &&
      previewMime === "application/pdf")
  );
};

export const describeAttachment = (attachment, index = 0) => {
  const fallbackBase =
    attachment?.filename || attachment?.name || `file-${index + 1}`;
  const trimmed = (fallbackBase || "").trim() || `file-${index + 1}`;
  const parts = trimmed.split(".");
  const hasExt = parts.length > 1;
  const extFromName = hasExt ? parts.pop() : "";
  const mimeExt = normalizeMime(attachment?.mime || "").split("/")[1] || "";
  const extension = (extFromName || mimeExt || "file").toLowerCase();
  const typeLabel = extension ? extension.toUpperCase() : "FILE";
  const baseName = hasExt ? parts.join(".") : trimmed;
  const displayName = hasExt ? trimmed : `${baseName}.${extension}`;
  return { typeLabel, displayName };
};

const AttachmentDownloadLink = ({
  attachment,
  index,
  isDark,
  onOpenDocument,
}) => {
  const href = attachment?.url || attachment?.previewUrl;
  const [downloading, setDownloading] = useState(false);
  if (!href) return null;

  const { typeLabel, displayName } = describeAttachment(attachment, index);
  const colors = isDark
    ? { border: "#333", bg: "rgba(255,255,255,0.03)", text: "#f5f5f5" }
    : { border: "#e5e7eb", bg: "#f8fafc", text: "#111827" };
  const isAudio = isAudioAttachment(attachment);

  const handleClick = async (e) => {
    e.preventDefault();
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await fetch(href);
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download attachment error:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (isAudio) {
    return (
      <Box
        sx={{
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          color: colors.text,
          borderRadius: 12,
          px: 1.5,
          py: 1.1,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Box sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{displayName}</Box>
        <audio controls src={href} style={{ width: "100%" }} />
        <Box
          component="button"
          onClick={handleClick}
          disabled={downloading}
          style={{
            alignSelf: "flex-start",
            border: "none",
            background: "transparent",
            color: colors.text,
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: 0,
            cursor: downloading ? "default" : "pointer",
          }}
        >
          {downloading ? "Downloading..." : "Download audio"}
        </Box>
      </Box>
    );
  }

  const opensInViewer =
    typeof onOpenDocument === "function" &&
    isViewableDocumentAttachment(attachment);

  if (opensInViewer) {
    return (
      <Box
        data-chat-document-attachment-card
        sx={{
          display: "flex",
          alignItems: "stretch",
          minWidth: 0,
          overflow: "hidden",
          borderRadius: 2,
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.bg,
          color: colors.text,
        }}
      >
        <ButtonBase
          data-chat-attachment-open-document
          onClick={() => onOpenDocument(attachment)}
          sx={{
            minWidth: 0,
            flex: 1,
            px: 1.2,
            py: 1,
            gap: 0.9,
            justifyContent: "flex-start",
            color: "inherit",
            textAlign: "left",
          }}
        >
          <DescriptionRoundedIcon sx={{ flexShrink: 0, fontSize: 21 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                color: "inherit",
                fontSize: "0.82rem",
                fontWeight: 700,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
            <Typography sx={{ color: "inherit", fontSize: "0.7rem", opacity: 0.68 }}>
              Open report in workspace
            </Typography>
          </Box>
        </ButtonBase>
        <Box sx={{ width: "1px", my: 0.75, backgroundColor: colors.border }} />
        <Tooltip title={`Download ${typeLabel}`} disableInteractive>
          <span style={{ display: "inline-flex", alignItems: "center" }}>
            <IconButton
              data-chat-attachment-download
              aria-label={`Download ${displayName}`}
              onClick={handleClick}
              disabled={downloading}
              sx={{
                width: 40,
                height: 40,
                alignSelf: "center",
                mx: 0.4,
                color: "inherit",
              }}
            >
              <DownloadRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bg,
        color: colors.text,
        fontSize: "0.9rem",
        fontWeight: 600,
        p: 2,
        textDecoration: "none",
        cursor: downloading ? "not-allowed" : "pointer",
        opacity: downloading ? 0.7 : 1,
      }}
    >
      <span>
        {downloading ? "Downloading..." : `Download the ${typeLabel}:`}
      </span>
      {!downloading && <span style={{ fontWeight: 700 }}>{displayName}</span>}
    </Box>
  );
};

const MessageAttachments = ({
  imageAttachments,
  fileAttachments,
  isDark,
  onOpenLightbox,
  onOpenDocument,
}) => {
  const hasImages = imageAttachments.length > 0;
  const hasFiles = fileAttachments.length > 0;

  if (!hasImages && !hasFiles) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.25 }}>
      {hasImages && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {imageAttachments.map((att, i) => {
            const src = att.thumbUrl || att.url || att.previewUrl;
            if (!src) return null;
            return (
              <button
                key={att.id || `${src}-${i}`}
                onClick={() => onOpenLightbox(i)}
                style={{
                  display: "inline-block",
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  lineHeight: 0,
                }}
                aria-label="View image attachment"
              >
                <img
                  src={src}
                  alt={att.name || att.filename || `attachment-${i + 1}`}
                  style={{
                    width: 84,
                    height: 84,
                    borderRadius: 10,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </button>
            );
          })}
        </Box>
      )}

      {hasFiles && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {fileAttachments.map((att, idx) => (
            <AttachmentDownloadLink
              key={att.id || `${att.filename || "file"}-${idx}`}
              attachment={att}
              index={idx}
              isDark={isDark}
              onOpenDocument={onOpenDocument}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MessageAttachments;
